<template>
    <div class="itemquantities-linegraph">
        <div v-if="loading" class="itemquantities-linegraph__overlay">
            <div class="spinner-border text-secondary" role="status">
                <span class="visually-hidden">Laster...</span>
            </div>
        </div>

        <div v-if="initialized && noDataForGroup" class="itemquantities-linegraph__empty">
            Ingen registreringer for denne gruppen ennå.
        </div>

        <div v-show="initialized && !noDataForGroup" class="itemquantities-linegraph__content">
            <div class="itemquantities-linegraph__filters">
                <label v-for="item of items" :key="item.id" class="itemquantities-linegraph__filter">
                    <input
                        type="checkbox"
                        :value="item.id"
                        v-model="selectedItemIds"
                        @change="fetchQuantities"
                    >
                    <img
                        v-if="item.illustration_src"
                        :src="item.illustration_src"
                        :alt="item.illustration_alt || item.name"
                        class="itemquantities-linegraph__filter-icon"
                    >
                    {{ item.name }}
                </label>

                <button type="button" class="itemquantities-linegraph__unit-btn" @click="toggleItems">
                    {{ allItemsSelected ? 'Velg ingen' : 'Velg alle' }}
                </button>
            </div>

            <div class="itemquantities-linegraph__filters">
                <label v-for="year of availableYears" :key="year" class="itemquantities-linegraph__filter">
                    <input type="checkbox" :value="year" v-model="selectedYears">
                    {{ year }}
                </label>

                <button type="button" class="itemquantities-linegraph__unit-btn" @click="toggleYears">
                    {{ allYearsSelected ? 'Velg ingen' : 'Velg alle' }}
                </button>
            </div>

            <div class="itemquantities-linegraph__filters">
                <button
                    type="button"
                    class="itemquantities-linegraph__unit-btn"
                    :class="{ 'itemquantities-linegraph__unit-btn--active': unit === 'kg' }"
                    @click="unit = 'kg'"
                >
                    Kg
                </button>
                <button
                    type="button"
                    class="itemquantities-linegraph__unit-btn"
                    :class="{ 'itemquantities-linegraph__unit-btn--active': unit === 'liters' }"
                    @click="unit = 'liters'"
                >
                    Liter
                </button>
            </div>

            <h2>Ukentlige mengder</h2>
            <highcharts :options="chartOptions" :highcharts="Highcharts"></highcharts>

            <h2>Årlige mengder</h2>
            <table class="itemquantities-linegraph__table">
                <thead>
                    <tr>
                        <th>Type</th>
                        <th v-for="year of sortedSelectedYears" :key="year">{{ year }}</th>
                        <th>Totalt ({{ unitLabel }})</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row of tableRows" :key="row.id">
                        <td class="itemquantities-linegraph__table-name">
                            <img
                                v-if="row.illustrationSrc"
                                :src="row.illustrationSrc"
                                :alt="row.illustrationAlt"
                                class="itemquantities-linegraph__filter-icon"
                            >
                            {{ row.name }}
                        </td>
                        <td v-for="(value, index) of row.yearValues" :key="index">{{ formatAmount(value) }}</td>
                        <td>{{ formatAmount(row.total) }}</td>
                    </tr>
                    <tr class="itemquantities-linegraph__table-total">
                        <td>Totalt ({{ unitLabel }})</td>
                        <td v-for="(value, index) of tableYearTotals" :key="index">{{ formatAmount(value) }}</td>
                        <td>{{ formatAmount(tableGrandTotal) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>
<script>
import { Chart } from 'highcharts-vue';
import Highcharts from '../../highcharts';

export default {
    components: {
        highcharts: Chart,
    },
    props: {
        groupId: {
            type: Number,
            required: true,
        },
    },
    created() {
        this.Highcharts = Highcharts;
    },
    data() {
        return {
            series: [],
            itemTotals: [],
            unit: 'kg',
            items: [],
            selectedItemIds: [],
            selectedYears: [],
            loading: true,
            initialized: false,
            noDataForGroup: false,
        };
    },
    computed: {
        unitLabel() {
            return this.unit === 'kg' ? 'kg' : 'liter';
        },
        availableYears() {
            return this.series.map(s => s.year);
        },
        allYearsSelected() {
            return this.availableYears.length > 0 && this.selectedYears.length === this.availableYears.length;
        },
        allItemsSelected() {
            return this.items.length > 0 && this.selectedItemIds.length === this.items.length;
        },
        sortedSelectedYears() {
            return [...this.selectedYears].sort((a, b) => a - b);
        },
        tableRows() {
            return this.itemTotals.map(item => {
                const yearValues = this.sortedSelectedYears.map(year => {
                    const entry = item.years.find(y => y.year === year);
                    return entry ? entry[this.unit] : 0;
                });

                const sourceItem = this.items.find(i => i.id === item.id);

                return {
                    id: item.id,
                    name: item.name,
                    illustrationSrc: sourceItem?.illustration_src,
                    illustrationAlt: sourceItem?.illustration_alt || item.name,
                    yearValues,
                    total: yearValues.reduce((sum, value) => sum + value, 0),
                };
            });
        },
        tableYearTotals() {
            return this.sortedSelectedYears.map((year, index) =>
                this.tableRows.reduce((sum, row) => sum + row.yearValues[index], 0)
            );
        },
        tableGrandTotal() {
            return this.tableRows.reduce((sum, row) => sum + row.total, 0);
        },
        chartOptions() {
            return {
                chart: {
                    type: 'line',
                },
                title: {
                    text: null,
                },
                xAxis: {
                    title: { text: 'Uke' },
                    type: 'linear',
                    allowDecimals: false,
                    tickInterval: 1,
                },
                yAxis: {
                    title: { text: `Mengde (${this.unitLabel})` },
                    min: 0,
                },
                series: this.series
                    .filter(s => this.selectedYears.includes(s.year))
                    .map(s => {
                        const trimmed = this.trimLeadingTrailingZeros(s.data);

                        return {
                            name: String(s.year),
                            data: trimmed.map(d => [d.week, d[this.unit]]),
                        };
                    }),
                credits: {
                    enabled: false,
                },
            };
        },
    },
    watch: {
        groupId() {
            this.init();
        },
    },
    mounted() {
        this.init();
    },
    methods: {
        async init() {
            this.loading = true;
            await this.fetchItems();
            this.selectedItemIds = this.items.map(item => item.id);
            await this.fetchQuantities();
            this.noDataForGroup = this.items.length === 0 || this.series.length === 0;
            this.initialized = true;
        },
        async fetchItems() {
            try {
                const response = await fetch(`/fetch/items?group=${this.groupId}`);
                this.items = await response.json();
            } catch (error) {
                console.error(error);
            }
        },
        async fetchQuantities() {
            if (this.selectedItemIds.length === 0) {
                this.series = [];
                this.itemTotals = [];
                this.selectedYears = [];
                this.loading = false;
                return;
            }

            this.loading = true;

            try {
                const params = new URLSearchParams();
                this.selectedItemIds.forEach(id => params.append('item_ids[]', id));

                const response = await fetch(`/api/v1/statistics/itemgroup/${this.groupId}/quantities?${params.toString()}`);
                const data = await response.json();
                this.series = data.series;
                this.itemTotals = data.items;
                this.selectedYears = this.series.map(s => s.year);
            } catch (error) {
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        toggleYears() {
            this.selectedYears = this.allYearsSelected ? [] : [...this.availableYears];
        },
        toggleItems() {
            this.selectedItemIds = this.allItemsSelected ? [] : this.items.map(item => item.id);
            this.fetchQuantities();
        },
        formatAmount(amount) {
            return Math.round(amount).toLocaleString('nb-NO');
        },
        trimLeadingTrailingZeros(data) {
            let start = 0;
            let end = data.length - 1;

            while (start <= end && data[start][this.unit] === 0) {
                start++;
            }

            while (end >= start && data[end][this.unit] === 0) {
                end--;
            }

            return data.slice(start, end + 1);
        },
    },
};
</script>
<style scoped>
.itemquantities-linegraph {
    position: relative;
}

.itemquantities-linegraph__overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.7);
}

.itemquantities-linegraph__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
}

.itemquantities-linegraph__filter {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    cursor: pointer;
}

.itemquantities-linegraph__filter-icon {
    width: 20px;
    height: 20px;
    object-fit: contain;
    vertical-align: middle;
    margin-right: 6px;
}

.itemquantities-linegraph__unit-btn {
    background: none;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 0.1rem 0.8rem;
    cursor: pointer;
}

.itemquantities-linegraph__unit-btn--active {
    background: #e9ecef;
    font-weight: bold;
}

.itemquantities-linegraph__empty {
    color: #6c757d;
}

.itemquantities-linegraph__table {
    width: 100%;
    margin-top: 1.5rem;
    border-collapse: collapse;
}

.itemquantities-linegraph__table th,
.itemquantities-linegraph__table td {
    text-align: right;
    padding: 0.4rem 0.6rem;
    border-bottom: 1px solid #dee2e6;
    border-right: 1px solid #dee2e6;
}

.itemquantities-linegraph__table th:first-child,
.itemquantities-linegraph__table td:first-child {
    text-align: left;
}

.itemquantities-linegraph__table th:last-child,
.itemquantities-linegraph__table td:last-child {
    border-right: none;
}

.itemquantities-linegraph__table-total {
    font-weight: bold;
}

.itemquantities-linegraph__table-name {
    white-space: nowrap;
}
</style>
